/**
 * TypeScript interfaces for AuditItemsFilters component
 *
 * Defines the data structures for filtering and sorting audit items
 * with support for multiple filter categories and sort options.
 */

import type { AuditItemStatus } from '@internal-audit/domain';
import type { MultiSelectOption, DropdownOption } from '@soteria/design-system-ve';

/**
 * Sort direction options (kept for compatibility but not used)
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Multiple decisions status for audit items
 */
export type MultipleDecisionsStatus = 'single' | 'none' | 'multiple';

/**
 * Page size options for pagination
 */
export type PageSize = 25 | 50 | 100;

/**
 * Pagination configuration for audit items
 */
export interface AuditItemPagination {
  currentPage: number;
  pageSize: PageSize;
}

/**
 * Comprehensive filter state for audit items
 * Updated to match API specification - single values only for most filters
 */
export interface AuditItemFilters {
  /** Status filter - single status value (API limitation) */
  status?: AuditItemStatus;
  /** Label filter - single label value (API limitation) */
  label?: string;
  /** Severity filter - single severity value (API limitation) */
  severity?: number;
  /** Multiple decisions status filter - single value like other filters */
  multipleDecisionsStatus?: MultipleDecisionsStatus;
  /** Policy violation filter - true for violations, false for passes, undefined for all */
  violatesPolicy?: boolean;
}

/**
 * Available filter options passed from parent component
 * Updated to match API specification
 */
export interface AuditItemFilterOptions {
  /** Available labels for selection */
  availableLabels: string[];
  /** Available severity levels */
  availableSeverities: number[];
  /** Available policy violation options */
  availableViolatesPolicyOptions: boolean[];
}

/**
 * Props for the main AuditItemsFilters component
 */
export interface AuditItemsFiltersProps {
  /** Current filter state */
  filters: AuditItemFilters;
  /** Callback when filters change */
  onFiltersChange: (filters: AuditItemFilters) => void;

  /** Current pagination configuration */
  pagination: AuditItemPagination;
  /** Callback when pagination changes */
  onPaginationChange: (pagination: AuditItemPagination) => void;

  /** Available options for filter dropdowns */
  filterOptions: AuditItemFilterOptions;

  /** Filter result counts for display */
  filteredCount?: number;
  totalCount?: number;

  /** Loading state to disable interactions */
  isLoading?: boolean;
}

/**
 * Helper type for creating empty filter state
 */
export const createEmptyAuditItemFilters = (): AuditItemFilters => ({
  status: undefined,
  label: undefined,
  severity: undefined,
  multipleDecisionsStatus: undefined,
  violatesPolicy: undefined,
});

/**
 * Helper type for creating default pagination state
 */
export const createDefaultAuditItemPagination = (): AuditItemPagination => ({
  currentPage: 1,
  pageSize: 100,
});

/**
 * Page size options for dropdown
 */
export const PAGE_SIZE_OPTIONS: DropdownOption[] = [
  { id: '25', label: '25 per page' },
  { id: '50', label: '50 per page' },
  { id: '100', label: '100 per page' },
];

/**
 * Status filter options - only show Skipped (no "All" option to prevent confusion)
 */
export const AUDIT_ITEM_STATUS_FILTER_OPTIONS: MultiSelectOption[] = [{ id: 'skipped', value: 'skipped', label: 'Skipped' }];

/**
 * Multiple decisions status filter options (no "All" option to prevent confusion)
 */
export const MULTIPLE_DECISIONS_FILTER_OPTIONS: MultiSelectOption[] = [
  { id: 'single', value: 'single', label: 'Single' },
  { id: 'none', value: 'none', label: 'None' },
  { id: 'multiple', value: 'multiple', label: 'Multiple' },
];

/**
 * Severity filter options (no "All" option to prevent confusion)
 */
export const SEVERITY_FILTER_OPTIONS: MultiSelectOption[] = [
  { id: '1', value: '1', label: '1' },
  { id: '2', value: '2', label: '2' },
  { id: '3', value: '3', label: '3' },
];
